import { HttpErrorResponse, HttpParams } from '@angular/common/http'
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core'
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms'
import { ToastService } from '@common/services'
import { ActivatedRoute, Router } from '@angular/router'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import { DemandIntentDocument, DemandIntentDocumentSearch, DemandIntentDocumentPublishStatus } from '@models/demand/demand-intent-document.model'
import { DemandIntentDocumentReport } from '@models/demand/demand-intent-document-report.model'
import { ManagementUnit } from '@models/pca/management-unit.model'
import { Paginate } from '@models/paginate.model'
import {
	ElectronicProcess,
	ElectronicProcessDocument,
	ElectronicProcessDocumentPartialRequest,
	ElectronicProcessDocumentRequest,
	ElectronicProcessDocumentMetadata,
	ElectronicProcessDocumentAttachment,
	ElectronicProcessDocumentPage,
	ElectronicProcessSearch,
	ElectronicProcessDocumentSourceType,
	ElectronicProcessDocumentStatus,
	ElectronicProcessDocumentVisibleType,
	ElectronicProcessPartialRequest,
	ElectronicProcessRequest,
	ElectronicProcessStatus
} from '@models/planning/electronic-process.model'
import {
	ElectronicProcessPaginationRun,
	ElectronicProcessPaginationRunStatus,
	ElectronicProcessPaginationRunType
} from '@models/planning/electronic-process-pagination.model'
import { AttachmentServiceType } from '@models/attachment/attachment.model'
import { UploadStatus } from '@models/attachment/upload-attachment.model'
import { AttachmentService } from '@services/attachment.service'
import { AlertifyService } from '@services/alertify.service'
import { AccountService } from '@services/account.service'
import { DemandIntentDocumentReportService } from '@services/demand-intent-document-report.service'
import { DemandIntentDocumentService } from '@services/demand-intent-document.service'
import { ElectronicProcessService } from '@services/electronic-process.service'
import { ElectronicProcessPaginationService } from '@services/electronic-process-pagination.service'
import { ElectronicProcessPaginationSocketService } from '@services/socket/electronic-process-pagination-socket.service'
import { RealtimeFallbackPoller } from '@services/realtime/realtime-fallback-poller'
import {
	ElectronicProcessPaginationSimulationComponent,
	ElectronicProcessPaginationSimulationResult
} from '@app/management/planning/electronic-processes/electronic-process-pagination/electronic-process-pagination-simulation.component'
import { ElectronicProcessPaginationHistoryComponent } from '@app/management/planning/electronic-processes/electronic-process-pagination/electronic-process-pagination-history.component'
import { ReportService } from '@services/report.service'
import { UploadS3Service } from '@services/upload-s3.service'
import { ZipEntry, ZipService } from '@services/zip.service'
import { debounceTime, distinctUntilChanged, pairwise, takeUntil } from 'rxjs/operators'
import { ManagementUnitService } from '@services/management-unit.service'
import { LabelService } from '@services/label.service'
import { TemplateUtil } from '@utils/template.util'
import { Subject, firstValueFrom } from 'rxjs'
import { saveAs } from 'file-saver'
import moment from 'moment/moment'
import { v4 as uuid } from 'uuid'
import { NgxMaskPipe } from 'ngx-mask'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'

export type ElectronicProcessTab = 'details' | 'documents' | 'pagination' | 'book'

@Component({
	selector: 'app-electronic-process-form',
	templateUrl: './electronic-process-form.component.html',
	styleUrl: './electronic-process-form.component.scss',
	standalone: false
})
export class ElectronicProcessFormComponent implements OnInit, OnDestroy {
	@Output() tabChange = new EventEmitter<ElectronicProcessTab>()
	@Output() processSaved = new EventEmitter<string>()
	@Output() processActionCompleted = new EventEmitter<void>()

	@Input() demand: DemandIntentDocument | null = null
	@Input() includeDocument = false
	@Input() includeDocumentExternalCode: string | null = null
	@Input() includeDocumentRequest: ElectronicProcessDocumentRequest | null = null

	@ViewChild('modalMergedDocument') modalMergedDocument?: TemplateRef<unknown>
	@ViewChildren('documentItem') documentItems?: QueryList<ElementRef<HTMLDivElement>>
	@ViewChild(ElectronicProcessPaginationHistoryComponent) paginationHistory?: ElectronicProcessPaginationHistoryComponent

	form = this.createProcessForm()
	documentForm = this.createDocumentForm()
	documentOtherFileControl = new FormControl<File | null>(null, Validators.compose([Validators.required]))

	loading = false
	units: ManagementUnit[] = []
	createDocument = false
	creatingDocument = false
	submittingProcess = false
	submittingDocuments = false
	downloadingAllAttachments = false
	linkedDemandLoading = false
	prefillingDemandDocuments = false
	activeTab: ElectronicProcessTab = 'details'
	movingDocumentFromIndex: number | null = null
	movingDocumentToIndex: number | null = null
	movingDocumentDirection: -1 | 1 | null = null
	savingDocumentMoveIndex: number | null = null
	dragPressIndex: number | null = null
	dragActiveIndex: number | null = null
	documentsForm = new FormArray([])
	linkedDemandIntentDocuments: DemandIntentDocument[] = []
	linkedDemand: DemandIntentDocument | string | null = null
	mergedDocumentUrl: SafeResourceUrl | null = null
	mergedDocumentFilename: string | null = null

	delayTapMoveMs = 2000

	readonly ElectronicProcessStatus = ElectronicProcessStatus
	readonly PaginationRunType = ElectronicProcessPaginationRunType

	readonly documentStatusOptions = [
		{ value: ElectronicProcessDocumentStatus.CREATED, label: 'Criado', badge: 'text-bg-secondary' },
		{ value: ElectronicProcessDocumentStatus.CANCELED, label: 'Cancelado', badge: 'text-bg-danger' }
	]

	readonly sourceTypeOptions = [
		{ value: ElectronicProcessDocumentSourceType.DEMAND, label: 'Demanda' },
		{ value: ElectronicProcessDocumentSourceType.DISPUTE, label: 'Disputa' },
		{ value: ElectronicProcessDocumentSourceType.CONTRACT, label: 'Contrato' },
		{ value: ElectronicProcessDocumentSourceType.OTHER, label: 'Outro' }
	]

	readonly visibleTypeOptions = [
		{ value: ElectronicProcessDocumentVisibleType.PUBLIC, label: 'Publico' },
		{ value: ElectronicProcessDocumentVisibleType.LGPD, label: 'LGPD' },
		{ value: ElectronicProcessDocumentVisibleType.PRIVATE, label: 'Privado' }
	]

	process: ElectronicProcess | null = null
	/** A execução de paginação mais recente do processo (spec 002): progresso, falhas e simulação pronta. */
	paginationRun: ElectronicProcessPaginationRun | null = null
	includeModeProcesses: ElectronicProcess[] = []
	includeModeProcessId: string | null = null
	includeModeProcessesLoading = false
	includeModeProcessTerm = ''
	includeModeProcessPage: Paginate<ElectronicProcess> | null = null

	private unitsLoaded = false
	private destroy$ = new Subject<void>()
	private readonly templateUtil = new TemplateUtil()
	private linkedDemandSearch$ = new Subject<string>()
	private moveDocumentAnimationTimeout: ReturnType<typeof setTimeout> | null = null
	private dragActivationTimeout: ReturnType<typeof setTimeout> | null = null
	private pendingDocumentReorder = false
	private dragPointerId: number | null = null
	private dragPointerStartX = 0
	private dragPointerStartY = 0
	private dragHandleElement: HTMLElement | null = null
	private originalDocumentPayloadById = new Map<string, string>()
	private includeDocumentHandled = false
	private includeDocumentDraftSourceId: string | null = null
	private includeDocumentScrollSourceId: string | null = null
	private includeDocumentScrollRetryTimeout: ReturnType<typeof setTimeout> | null = null
	private mergedDocumentObjectUrl: string | null = null
	private mergedDocumentBlob: Blob | null = null
	private documentsById = new Map<string, ElectronicProcessDocument>()
	private paginationPoller?: RealtimeFallbackPoller
	private paginationProcessId: string | null = null
	private simulationModalOpen = false
	/** A execução mais recente já foi buscada (mesmo que não exista nenhuma). */
	private paginationRunLoaded = false

	constructor(
		private router: Router,
		private mask: NgxMaskPipe,
		private label: LabelService,
		private toast: ToastService,
		private route: ActivatedRoute,
		private alertfy: AlertifyService,
		private modal: NgbModal,
		private sanitizer: DomSanitizer,
		private attachmentService: AttachmentService,
		private accountService: AccountService,
		private reportService: ReportService,
		private uploadS3Service: UploadS3Service,
		private zipService: ZipService,
		private managementUnitService: ManagementUnitService,
		private electronicProcessService: ElectronicProcessService,
		private paginationService: ElectronicProcessPaginationService,
		private paginationSocket: ElectronicProcessPaginationSocketService,
		private demandIntentDocumentService: DemandIntentDocumentService,
		private demandIntentDocumentReportService: DemandIntentDocumentReportService
	) {}

	ngOnInit(): void {
		if (this.includeDocument) {
			this.activeTab = 'documents'
			this.ensureIncludeDocumentDraft()
			void this.searchIncludeModeProcesses()
		}

		this.linkedDemandSearch$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(term => {
			void this.searchDemandIntentDocuments(term)
		})

		this.form
			.get('demandIntentDocumentId')
			?.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe(value => {
				if (this.demandId || !value || this.linkedDemandId === value) {
					return
				}

				void this.syncLinkedDemandById(value)
			})

		this.form.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
			if (this.activeTab === 'documents' && this.form.invalid) {
				this.setTab('details')
			}
		})

		// Paginação em tempo real (spec 002): cada mudança de uma execução do processo aberto chega pelo tópico dele.
		this.paginationSocket.events$.pipe(takeUntil(this.destroy$)).subscribe(run => void this.onPaginationEvent(run))

		// De volta depois de uma queda: busca o que passou fora do ar.
		this.paginationSocket.status$.pipe(pairwise(), takeUntil(this.destroy$)).subscribe(([previous, current]) => {
			if (previous !== 'connected' && current === 'connected') void this.resyncPagination()
		})

		void this.ensureUnitsLoaded()
	}

	ngOnDestroy(): void {
		this.stopWatchingPagination()
		void this.paginationSocket.disconnect()

		if (this.moveDocumentAnimationTimeout) {
			clearTimeout(this.moveDocumentAnimationTimeout)
		}
		if (this.includeDocumentScrollRetryTimeout) {
			clearTimeout(this.includeDocumentScrollRetryTimeout)
		}
		this.clearMergedDocumentPreview()

		this.clearDocumentDragInteraction()
		this.pendingDocumentReorder = false
		this.savingDocumentMoveIndex = null

		this.destroy$.next()
		this.destroy$.complete()
	}

	addNewDocument() {
		this.createDocument = true
		this.documentForm = this.createDocumentForm()
		this.documentOtherFileControl.reset(null)
	}

	setActiveTab(tab: ElectronicProcessTab): void {
		this.activeTab = tab
	}

	markProcessFormTouched(): void {
		this.form.markAllAsTouched()
	}

	markDocumentFormTouched(): void {
		this.documentForm.markAllAsTouched()
		this.documentOtherFileControl.markAsTouched()
	}

	appendDraftDocumentFromForm(): void {
		const rawValue = this.documentForm.getRawValue()
		const sourceType = Number(rawValue.sourceType ?? ElectronicProcessDocumentSourceType.OTHER) as ElectronicProcessDocumentSourceType

		this.documentsForm.push(
			this.createDocumentGroup(
				new ElectronicProcessDocument({
					title: rawValue.title,
					description: rawValue.description,
					ordering: this.nextOrderingForSourceType(sourceType),
					status: ElectronicProcessDocumentStatus.CREATED,
					sourceId: rawValue.sourceId,
					metadata: rawValue.metadata ?? null,
					sourceType,
					visibleType: rawValue.visibleType,
					date: rawValue.date
				})
			)
		)

		this.recalculateDocumentOrdering(this.documentsForm)
		this.resetDocumentForm()
	}

	buildProcessRequest(): ElectronicProcessRequest {
		const rawValue = this.form.getRawValue()
		const unit = this.units.find(item => item.id === rawValue.unitId)

		return new ElectronicProcessRequest({
			unitId: rawValue.unitId,
			unitName: unit?.name ?? null,
			externalCode: rawValue.externalCode?.trim() ?? '',
			object: rawValue.object?.trim() ?? '',
			demandIntentDocumentId: rawValue.demandIntentDocumentId?.trim() || null
		})
	}

	buildDocumentsPayload(): ElectronicProcessDocumentRequest[] {
		return this.documentsForm.controls.map(control => this.buildDocumentPayload(control.getRawValue()))
	}

	buildDocumentFilename(value: any): string {
		const parts = [value.ordering, value.externalCode || value.title || 'documento']
		return `${parts.filter(Boolean).join(' - ')}.pdf`
	}

	rebuildDocumentsForm(documents: ElectronicProcessDocument[]): void {
		this.documentsForm.clear()
		this.indexDocuments(documents)

		const sortedDocuments = [...documents].sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0))

		for (const document of sortedDocuments) {
			this.documentsForm.push(this.createDocumentGroup(document))
		}

		this.captureDocumentsSnapshot()
		this.updateDocumentsReadonlyState()
	}

	recalculateDocumentOrdering(formArray = this.documentsForm): void {
		formArray.controls.forEach((control, index) => {
			control.get('ordering')?.setValue(index + 1)
		})
	}

	resetDocumentForm(): void {
		this.documentForm.reset({
			title: null,
			description: null,
			sourceId: null,
			metadata: null,
			sourceType: ElectronicProcessDocumentSourceType.OTHER,
			visibleType: ElectronicProcessDocumentVisibleType.PUBLIC,
			date: new Date().toISOString().slice(0, 10)
		})
		this.documentOtherFileControl.reset(null)

		if (this.canAddDocuments) {
			this.documentForm.enable()
			this.documentForm.get('sourceType')?.disable()
		} else {
			this.documentForm.disable()
		}
	}

	onLinkedDemandSearch(term: string): void {
		if (this.isReadonly || this.lockDemandAndUnit) {
			return
		}

		this.linkedDemandSearch$.next(term?.trim() ?? '')
	}

	setTab(tab: ElectronicProcessTab): void {
		if (this.includeDocument && tab === 'details') {
			return
		}

		if (tab === 'documents' && !this.canOpenDocumentsTab) {
			this.markProcessFormTouched()
			return
		}

		this.activeTab = tab
		this.tabChange.emit(tab)
	}

	deleteProcess(): void {
		if (!this.process?.id) {
			return
		}

		this.alertfy.confirm('Excluir processo', 'Deseja excluir este processo eletrônico?', event => {
			if (!event.confirmed) {
				return
			}

			void this.runProcessAction(async () => {
				await firstValueFrom(this.electronicProcessService.remove(this.process!.id!))
				this.toast.success('Processo eletrônico excluido com sucesso.')

				if (this.isDemandContext) {
					this.processActionCompleted.emit()
					return
				}

				this.backToList()
			}, false)
		})
	}

	/**
	 * Reprocessar paginação (spec 002, RF 12 a 18): com erro na publicação, publica de novo; em processo publicado, simula
	 * a renumeração sem alterar nada e abre a revisão.
	 */
	reprocessDocumentPagination(): void {
		if (!this.process?.id || !this.canReprocessPagination) {
			return
		}

		const simulation = this.process.status === ElectronicProcessStatus.PUBLISHED
		const [title, message] = simulation
			? [
					'Reprocessar paginação',
					'O sistema vai contar as páginas de todo o processo, sem alterar nada, e mostrar as folhas que mudariam. Você revisa e decide se aplica. Deseja continuar?'
				]
			: ['Publicar novamente', 'Deseja publicar este processo eletrônico novamente? A contagem de páginas de todos os documentos é refeita.']

		this.alertfy.confirm(title, message, event => {
			if (!event.confirmed) {
				return
			}

			void this.runProcessAction(async () => {
				try {
					await firstValueFrom(this.electronicProcessService.reprocessDocumentPagination(this.process!.id!))
				} catch (error) {
					this.handlePaginationRequestError(error)
					await this.reloadCurrentProcess()
					return
				}

				await this.reloadCurrentProcess()

				if (simulation && this.paginationRun?.type === ElectronicProcessPaginationRunType.SIMULATION) {
					this.openSimulation(this.paginationRun)
				} else {
					this.toast.info('Publicação iniciada. A contagem de páginas segue em segundo plano; acompanhe o andamento nesta tela.')
				}
			}, false)
		})
	}

	/** Abre a simulação (em andamento ou pronta) para acompanhar, revisar e aplicar. */
	openSimulation(run: ElectronicProcessPaginationRun | null = this.paginationRun): void {
		if (!this.process?.id || !run?.id || this.simulationModalOpen) {
			return
		}

		const modalRef = this.modal.open(ElectronicProcessPaginationSimulationComponent, { size: 'lg', centered: true, backdrop: 'static' })
		const component = modalRef.componentInstance as ElectronicProcessPaginationSimulationComponent
		component.processId = this.process.id
		component.run = run
		component.applyBlockedReason = this.getChangedDocumentControls().length
			? 'Há alterações não salvas nos documentos. Salve ou descarte essas alterações antes de aplicar a renumeração.'
			: null

		this.simulationModalOpen = true
		modalRef.result
			.then((result: ElectronicProcessPaginationSimulationResult) => {
				if (result === 'applied') void this.refreshAfterPagination()
			})
			.catch(() => undefined)
			.finally(() => (this.simulationModalOpen = false))
	}

	/** Nova tentativa de paginação de documento com falha, em processo publicado (RF 10). */
	retryDocumentPagination(documentControl: FormGroup): void {
		const documentId = documentControl.get('id')?.value

		if (!this.process?.id || !documentId || !this.canRetryDocumentPagination(documentControl)) {
			return
		}

		void this.runDocumentAction(async () => {
			documentControl.get('submitting')?.setValue(true)
			await firstValueFrom(this.paginationService.retryDocument(this.process!.id!, documentId))
			this.toast.info('Documento enviado de novo para a paginação.')
		})
	}

	/** A situação de paginação do documento da linha, como veio do servidor. */
	documentPagination(documentControl: any): ElectronicProcessDocument | null {
		const id = documentControl?.get?.('id')?.value
		return id ? (this.documentsById.get(id) ?? null) : null
	}

	canRetryDocumentPagination(documentControl: any): boolean {
		return this.process?.status === ElectronicProcessStatus.PUBLISHED && !!this.documentPagination(documentControl)?.paginationFailed
	}

	/** Em processo publicado, só sai o documento incluído cuja paginação falhou: ele ainda não tem folhas (RF 10). */
	canRemoveDocument(documentControl: any): boolean {
		return this.canEditDocumentList || this.canRetryDocumentPagination(documentControl)
	}

	backToList(): void {
		this.stopWatchingPagination()
		this.process = null

		void this.router.navigate([], {
			relativeTo: this.route,
			queryParams: {}
		})
	}

	async moveDocument(index: number, direction: -1 | 1): Promise<void> {
		if (this.submittingDocuments) {
			return
		}

		const targetIndex = this.reorderDocument(index, direction)

		if (targetIndex == null || !this.process?.id || !this.canReorderDocuments) {
			return
		}

		await this.persistDocumentReorder(targetIndex)
	}

	startDocumentDragPress(event: PointerEvent, index: number): void {
		if (!this.canReorderDocuments || this.submittingDocuments || event.button > 0) {
			return
		}

		this.clearDocumentDragInteraction()
		this.dragPressIndex = index
		this.dragPointerId = event.pointerId
		this.dragPointerStartX = event.clientX
		this.dragPointerStartY = event.clientY
		this.dragHandleElement = event.currentTarget as HTMLElement
		this.dragHandleElement?.setPointerCapture?.(event.pointerId)

		this.dragActivationTimeout = setTimeout(() => {
			this.dragActiveIndex = index
		}, this.delayTapMoveMs)
	}

	onDocumentDragPointerMove(event: PointerEvent): void {
		if (this.submittingDocuments || this.dragPointerId !== event.pointerId) {
			return
		}

		if (this.dragActiveIndex == null) {
			const movedX = Math.abs(event.clientX - this.dragPointerStartX)
			const movedY = Math.abs(event.clientY - this.dragPointerStartY)

			if (movedX > 8 || movedY > 8) {
				this.clearDocumentDragInteraction()
			}

			return
		}

		event.preventDefault()

		const hoveredElement = document.elementFromPoint(event.clientX, event.clientY) as HTMLElement | null
		const hoveredItem = hoveredElement?.closest('[data-document-index]') as HTMLElement | null

		if (!hoveredItem) {
			return
		}

		const targetIndex = Number(hoveredItem.dataset['documentIndex'])

		if (Number.isNaN(targetIndex) || targetIndex === this.dragActiveIndex) {
			return
		}

		const direction = targetIndex > this.dragActiveIndex ? 1 : -1
		const reorderedToIndex = this.reorderDocument(this.dragActiveIndex, direction)

		if (reorderedToIndex == null) {
			return
		}

		this.pendingDocumentReorder = true
		this.dragActiveIndex = reorderedToIndex
	}

	async finishDocumentDragPress(event?: PointerEvent): Promise<void> {
		if (event && this.dragPointerId != null && event.pointerId !== this.dragPointerId) {
			return
		}

		const shouldPersistReorder = this.pendingDocumentReorder && !!this.process?.id && this.canReorderDocuments && this.dragActiveIndex != null
		const reorderTargetIndex = this.dragActiveIndex

		this.clearDocumentDragInteraction()

		if (shouldPersistReorder && reorderTargetIndex != null) {
			await this.persistDocumentReorder(reorderTargetIndex)
		}
	}

	removeDocument(index: number, documentControl?: FormGroup): void {
		if (!this.canRemoveDocument(documentControl)) {
			return
		}

		const documentId = documentControl.get('id')?.value

		if (!this.process?.id || !documentId) {
			this.documentsForm.removeAt(index)
			this.recalculateDocumentOrdering()
			return
		}

		this.alertfy.confirm('Excluir documento', 'Deseja excluir este documento do processo?', event => {
			if (!event.confirmed) {
				return
			}

			void this.runDocumentAction(async () => {
				documentControl.get('submitting')?.setValue(true)

				await firstValueFrom(this.electronicProcessService.removeDocument(this.process!.id!, documentId))
				this.toast.success('Documento excluido com sucesso.')

				this.documentsForm.removeAt(index)
				this.recalculateDocumentOrdering()

				documentControl.get('submitting')?.setValue(false)
			})
		})
	}

	cancelDocument(documentControl: FormGroup): void {
		if (!this.process?.id || !this.canManageDocuments) {
			return
		}

		this.alertfy.confirm('Cancelar documento', 'Deseja cancelar este documento do processo?', event => {
			if (!event.confirmed) {
				return
			}

			const documentId = documentControl.get('id')?.value

			documentControl.get('submitting')?.setValue(true)

			void this.runDocumentAction(async () => {
				await firstValueFrom(this.electronicProcessService.cancelDocument(this.process!.id!, documentId))
				this.toast.success('Documento cancelado com sucesso.')
				documentControl.get('submitting')?.setValue(false)
			})
		})
	}

	trackByDocument(index: number): number {
		return index
	}

	documentStatusLabel(status?: ElectronicProcessDocumentStatus): string {
		return this.documentStatusOptions.find(option => option.value === status)?.label ?? 'Nao informado'
	}

	documentStatusBadge(status?: ElectronicProcessDocumentStatus): string {
		return this.documentStatusOptions.find(option => option.value === status)?.badge ?? 'text-bg-light'
	}

	visibleTypeLabel(visibleType?: ElectronicProcessDocumentVisibleType): string {
		return this.visibleTypeOptions.find(option => option.value === visibleType)?.label ?? 'Nao informado'
	}

	statusLabel(status?: ElectronicProcessStatus): string {
		return this.label.fromElectronicProcessesStatuses(status)?.label ?? 'Nao informado'
	}

	statusBadge(status?: ElectronicProcessStatus): string {
		return this.label.fromElectronicProcessesStatuses(status)?.data?.badge ?? 'text-bg-light'
	}

	async onLinkedDemandChange(value: DemandIntentDocument | string | null): Promise<void> {
		if (this.lockDemandAndUnit) {
			return
		}

		if (!value) {
			this.linkedDemand = null
			this.form.get('demandIntentDocumentId')?.setValue(null, { emitEvent: false })

			if (this.prefillDocuments) {
				this.documentsForm.clear()
			}
			return
		}

		if (typeof value === 'string') {
			await this.resolveLinkedDemandByExternalCode(value)
			return
		}

		if (!value.id) {
			await this.resolveLinkedDemandByExternalCode(value.externalCode ?? '')
			return
		}

		this.linkedDemand = value
		this.appendLinkedDemandOption(value)
		this.form.get('demandIntentDocumentId')?.setValue(value.id, { emitEvent: false })
		this.syncUnitFromDemand(value, true)

		if (this.prefillDocuments) {
			await this.fillDocumentsFromDemandReports(value.id)
		}
	}

	async handlePrimaryAction(): Promise<void> {
		if (this.prefillingDemandDocuments) {
			return
		}

		if (this.isCreateMode && this.activeTab === 'details') {
			if (this.form.invalid) {
				this.markProcessFormTouched()
				return
			}

			this.setTab('documents')
			return
		}

		if (this.documentsForm.controls.some(it => it.invalid)) {
			this.documentsForm.markAllAsTouched()
			return
		}

		await this.saveProcess()

		this.process = this?.currentProcess ?? this.process
	}

	async ensureUnitsLoaded(): Promise<void> {
		if (this.unitsLoaded) {
			return
		}

		const params = new HttpParams().set('page', 0).set('size', 200)
		const response = await firstValueFrom(this.managementUnitService.search(new ManagementUnit(), params))
		this.units = (response.content ?? []).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
		this.unitsLoaded = true
	}

	async prepareCreate(demandId: string | null = null): Promise<void> {
		await this.ensureUnitsLoaded()
		this.process = null
		this.demandId = demandId?.trim() || null
		this.form.reset({
			unitId: null,
			externalCode: null,
			object: null,
			demandIntentDocumentId: this.demandId
		})
		this.form.enable()
		this.documentsForm.clear()
		this.pendingDocumentReorder = false
		this.savingDocumentMoveIndex = null
		this.linkedDemand = null
		this.linkedDemandIntentDocuments = []
		await this.initializeLinkedDemand()
		this.resetDocumentForm()
		this.updateDocumentsReadonlyState()

		if (this.includeDocument) {
			await this.prepareIncludeDocumentMode()
		}
	}

	async loadProcess(id: string, demandId: string | null = null): Promise<void> {
		this.loading = true

		try {
			this.process = await firstValueFrom(this.electronicProcessService.detail(id))

			await this.ensureUnitsLoaded()

			this.demandId = demandId?.trim() || null

			this.form.patchValue({
				unitId: this.process.unitId ?? null,
				object: this.process.object ?? null,
				externalCode: this.process.externalCode ?? null,
				demandIntentDocumentId: this.process.demandIntentDocumentId ?? null
			})

			if (this.canEditProcess) {
				this.form.enable()
			} else {
				this.form.disable()
			}

			this.rebuildDocumentsForm(this.process.documents ?? [])
			this.pendingDocumentReorder = false
			this.savingDocumentMoveIndex = null

			await this.watchPagination()

			await this.initializeLinkedDemand()

			this.resetDocumentForm()

			if (this.includeDocument) {
				this.activeTab = 'documents'
				this.ensureIncludeDocumentDraft()
			}
		} finally {
			this.loading = false
		}
	}

	async prepareIncludeDocumentMode(): Promise<void> {
		if (!this.includeDocument) {
			return
		}

		this.activeTab = 'documents'

		const externalCode = this.includeDocumentExternalCode?.trim() ?? ''

		if (this.process?.id) {
			this.ensureIncludeDocumentDraft()
			return
		}

		// Planejamento: mantém carregamento direto por código quando existir vínculo do demand.
		if (externalCode && this.demandId) {
			await this.loadProcessByExternalCode(externalCode)
			return
		}

		await this.searchIncludeModeProcesses(externalCode)
	}

	async includeDocumentInProcess(): Promise<void> {
		const includeDraftControl = this.getIncludeDocumentDraftControl()

		if (includeDraftControl?.invalid) {
			includeDraftControl.markAllAsTouched()
			return
		}

		await this.tryIncludeDocumentRequest()
	}

	async saveProcess(): Promise<void> {
		if (this.form.invalid) {
			this.markProcessFormTouched()
			return
		}

		this.submittingProcess = true

		try {
			const payload = this.buildProcessRequest()

			if (this.process?.id) {
				await firstValueFrom(this.electronicProcessService.update(this.process.id, new ElectronicProcessPartialRequest(payload)))
				this.toast.success('Processo eletrônico atualizado com sucesso.')
				await this.reloadCurrentProcess()

				if (this.demand?.id && this.process?.id) {
					this.processSaved.emit(this.process.id)
				}

				return
			}

			const response = await firstValueFrom(this.electronicProcessService.save(new ElectronicProcessRequest(payload)))
			const processId = response?.id

			if (processId && this.documentsForm.length > 0) {
				await firstValueFrom(this.electronicProcessService.saveDocuments(processId, this.buildDocumentsPayload()))
			}

			this.toast.success('Processo eletrônico criado com sucesso.')

			if (processId) {
				if (this.demand?.id) {
					this.processSaved.emit(processId)
					return
				}

				await this.router.navigate([], {
					relativeTo: this.route,
					queryParams: { id: processId, tab: 'documents' }
				})
			}
		} finally {
			this.submittingProcess = false
		}
	}

	async addDocument(): Promise<void> {
		if (!this.canAddDocuments) {
			return
		}

		if (this.documentForm.invalid || this.documentOtherFileControl.invalid) {
			this.markDocumentFormTouched()
			return
		}

		this.creatingDocument = true

		try {
			const uploadResult = await this.uploadOtherDocumentAttachments()

			if (!uploadResult) {
				return
			}

			this.documentForm.patchValue({
				sourceId: uploadResult.sourceId,
				metadata: uploadResult.metadata
			})

			if (!this.process?.id) {
				this.appendDraftDocumentFromForm()
				this.createDocument = false
				return
			}

			const payload = this.buildDocumentRequest()
			await firstValueFrom(this.electronicProcessService.saveDocuments(this.process.id, [payload]))
			this.toast.success('Documento incluido com sucesso.')
			this.resetDocumentForm()
			await this.reloadCurrentProcess()
			this.createDocument = false
			this.scheduleIncludeDocumentFormScroll(uploadResult.sourceId)
		} finally {
			this.creatingDocument = false
		}
	}

	async saveDocuments(): Promise<void> {
		if (!this.process?.id || !this.canPersistDocumentChanges || this.documentsForm.length === 0) {
			return
		}

		if (this.getChangedDocumentControls().length === 0) {
			return
		}

		this.submittingDocuments = true

		try {
			await this.persistDocumentsChanges(true)
		} finally {
			this.submittingDocuments = false
		}
	}

	async publishProcess(): Promise<void> {
		if (
			!this.process?.id ||
			[this.ElectronicProcessStatus.PUBLISHED, this.ElectronicProcessStatus.IN_PUBLISH].includes(this.process.status as ElectronicProcessStatus)
		) {
			return
		}

		this.alertfy.confirm('Publicar processo', 'Deseja publicar este processo eletrônico?', event => {
			if (!event.confirmed) {
				return
			}

			void this.runProcessAction(async () => {
				try {
					await firstValueFrom(this.electronicProcessService.publish(this.process!.id!))
					this.toast.info('Publicação iniciada. A contagem de páginas segue em segundo plano; acompanhe o andamento nesta tela.')
				} catch (error) {
					this.handlePaginationRequestError(error)
				}

				await this.reloadCurrentProcess()
			}, false)
		})
	}

	async cancelProcess(): Promise<void> {
		if (!this.process?.id || this.process.status === ElectronicProcessStatus.CANCELED) {
			return
		}

		this.alertfy.confirm('Cancelar processo', 'Deseja cancelar este processo eletrônico?', event => {
			if (!event.confirmed) {
				return
			}

			void this.runProcessAction(async () => {
				await firstValueFrom(this.electronicProcessService.cancel(this.process!.id!))
				this.toast.success('Processo eletrônico cancelado com sucesso.')

				if (this.isDemandContext) {
					this.processActionCompleted.emit()
				}
			})
		})
	}

	async downloadDocument(documentControl: FormGroup): Promise<void> {
		const sourceId = documentControl.get('sourceId')?.value

		if (!sourceId) {
			return
		}

		documentControl.get('submitting')?.setValue(true)

		try {
			const filename = this.buildDocumentFilename(documentControl.getRawValue())
			const fileBlob = await firstValueFrom(this.attachmentService.downloadHandleResponseBlob(sourceId))
			this.openMergedDocumentPreview(fileBlob, filename)
		} finally {
			documentControl.get('submitting')?.setValue(false)
		}
	}

	async downloadDocumentAttachment(documentControl: FormGroup, attachment: ElectronicProcessDocumentAttachment): Promise<void> {
		if (!attachment?.id) {
			return
		}

		documentControl.get('submitting')?.setValue(true)

		try {
			const fileBlob = await firstValueFrom(this.attachmentService.downloadHandleResponseBlob(attachment.id))
			const filename = attachment.filename || `${attachment.name || 'anexo'}.pdf`
			saveAs(fileBlob, filename)
		} finally {
			documentControl.get('submitting')?.setValue(false)
		}
	}

	async downloadAllAttachmentsZip(): Promise<void> {
		const files = this.getProcessAttachmentFiles()

		if (!files.length) {
			this.toast.warn('Não localizei anexos para download.')
			return
		}

		this.downloadingAllAttachments = true
		try {
			const entries: ZipEntry[] = []

			for (const file of files) {
				try {
					const fileBlob = await firstValueFrom(this.attachmentService.downloadHandleResponseBlob(file.id))
					entries.push({ name: file.name, blob: fileBlob })
				} catch (e) {
					console.error(e)
				}
			}

			if (!entries.length) {
				this.toast.warn('Não foi possível preparar os anexos para download.')
				return
			}

			const processCode = this.sanitizeZipFilename(this.process?.externalCode || moment().format('YYYYMMDDHHmmss')).toLowerCase()
			await this.zipService.downloadZip(entries, `anexos-processo-eletronico-${processCode}.zip`, 7)
		} finally {
			this.downloadingAllAttachments = false
		}
	}

	getDocumentAttachments(documentControl: FormGroup): ElectronicProcessDocumentAttachment[] {
		const metadata = documentControl?.getRawValue?.()?.metadata as ElectronicProcessDocumentMetadata | undefined
		return metadata?.attachments ?? []
	}

	getDocumentAttachmentPagesTotal(documentControl: FormGroup): number {
		return this.getDocumentAttachments(documentControl).reduce((sum, attachment) => sum + Number(attachment?.page?.total ?? 0), 0)
	}

	isOtherDocument(documentControl: FormGroup): boolean {
		return Number(documentControl.get('sourceType')?.value) === Number(ElectronicProcessDocumentSourceType.OTHER)
	}

	private get linkedDemandId(): string | null {
		return typeof this.linkedDemand === 'string' ? null : (this.linkedDemand?.id ?? null)
	}

	private buildDocumentRequest(): ElectronicProcessDocumentRequest {
		const rawValue = this.documentForm.getRawValue()
		const sourceType = Number(rawValue.sourceType ?? ElectronicProcessDocumentSourceType.OTHER) as ElectronicProcessDocumentSourceType

		return this.buildDocumentPayload({
			...rawValue,
			sourceType,
			ordering: this.nextOrderingForSourceType(sourceType)
		})
	}

	private async initializeLinkedDemand(): Promise<void> {
		const inputDemandId = this.demand?.id?.trim() || this.demandId?.trim() || null

		if (inputDemandId) {
			this.form.get('demandIntentDocumentId')?.setValue(inputDemandId)
			await this.syncLinkedDemandById(inputDemandId, true)
			return
		}

		const linkedDemandId = this.form.get('demandIntentDocumentId')?.value

		if (linkedDemandId) {
			await this.syncLinkedDemandById(linkedDemandId)
			return
		}

		this.linkedDemand = null
		this.linkedDemandIntentDocuments = []
	}

	private createProcessForm(): FormGroup {
		return new FormGroup({
			unitId: new FormControl<string | null>(null, Validators.compose([Validators.required])),
			externalCode: new FormControl<string | null>(null, Validators.compose([Validators.required])),
			object: new FormControl<string | null>(null, Validators.compose([Validators.required])),
			demandIntentDocumentId: new FormControl<string | null>(null, Validators.compose([Validators.required]))
		})
	}

	private createDocumentForm(): FormGroup {
		return new FormGroup({
			description: new FormControl<string | null>(null),
			title: new FormControl<string | null>(null, Validators.compose([Validators.required])),
			sourceId: new FormControl<string | null>(null),
			metadata: new FormControl<ElectronicProcessDocumentMetadata | null>(null),
			sourceType: new FormControl<ElectronicProcessDocumentSourceType | null>(
				{ value: ElectronicProcessDocumentSourceType.OTHER, disabled: true },
				Validators.compose([Validators.required])
			),
			visibleType: new FormControl<ElectronicProcessDocumentVisibleType>(
				ElectronicProcessDocumentVisibleType.PUBLIC,
				Validators.compose([Validators.required])
			),
			date: new FormControl<string | null>(moment().format('YYYY-MM-DD'), Validators.compose([Validators.required]))
		})
	}

	private createDocumentGroup(document: ElectronicProcessDocument): FormGroup {
		const control = new FormGroup({
			id: new FormControl(document.id ?? null),
			submitting: new FormControl(false),
			description: new FormControl(document.description ?? null),
			status: new FormControl(document.status ?? ElectronicProcessDocumentStatus.CREATED),
			title: new FormControl(document.title ?? null, Validators.compose([Validators.required])),
			ordering: new FormControl(document.ordering ?? 0, Validators.compose([Validators.required])),
			sourceId: new FormControl(document.sourceId ?? null, Validators.compose([Validators.required])),
			metadata: new FormControl(document.metadata ?? null),
			date: new FormControl(document.date ?? new Date().toISOString().slice(0, 10), Validators.compose([Validators.required])),
			sourceType: new FormControl(document.sourceType ?? ElectronicProcessDocumentSourceType.OTHER, Validators.compose([Validators.required])),
			visibleType: new FormControl(document.visibleType ?? ElectronicProcessDocumentVisibleType.PUBLIC, Validators.compose([Validators.required])),
			page: new FormControl<ElectronicProcessDocumentPage>(document.page),
			hasPage: new FormControl<boolean>(document.hasPage)
		})

		if (!this.canEditDocumentList) {
			control.disable()
		} else {
			control.get('id')?.disable()
			control.get('status')?.disable()
			control.get('sourceId')?.disable()
			control.get('sourceType')?.disable()
		}

		return control
	}

	private buildDocumentPayload(value: any): ElectronicProcessDocumentRequest {
		return new ElectronicProcessDocumentRequest(this.buildDocumentPayloadData(value))
	}

	private reorderDocument(index: number, direction: -1 | 1): number | null {
		const targetIndex = index + direction

		if (targetIndex < 0 || targetIndex >= this.documentsForm.length) {
			return null
		}

		const current = this.documentsForm.at(index) as FormGroup
		const target = this.documentsForm.at(targetIndex) as FormGroup

		const currentValue = current.getRawValue()
		const targetValue = target.getRawValue()

		current.patchValue(targetValue)
		target.patchValue(currentValue)
		this.recalculateDocumentOrdering()
		this.highlightMovedDocument(index, targetIndex, direction)

		return targetIndex
	}

	private async persistDocumentReorder(targetIndex: number): Promise<void> {
		this.pendingDocumentReorder = false
		this.savingDocumentMoveIndex = targetIndex

		try {
			await this.runDocumentAction(async () => {
				await this.persistDocumentsChanges(false)
			})
		} finally {
			this.savingDocumentMoveIndex = null
		}
	}

	private async reloadCurrentProcess(): Promise<void> {
		if (!this.process?.id) {
			return
		}

		await this.loadProcess(this.process.id, this.demandId)
	}

	private async loadProcessByExternalCode(externalCode: string): Promise<void> {
		this.loading = true

		try {
			const params = new HttpParams().set('page', 0).set('size', 30)
			const response = await firstValueFrom(this.electronicProcessService.paginate(new ElectronicProcessSearch({ externalCode }), params))
			const normalizedExternalCode = externalCode.trim().toUpperCase()

			const process =
				response.content?.find(
					item =>
						item.externalCode?.trim().toUpperCase() === normalizedExternalCode && (!this.demandId || item.demandIntentDocumentId === this.demandId)
				) ??
				response.content?.find(item => !this.demandId || item.demandIntentDocumentId === this.demandId) ??
				null

			if (!process?.id) {
				this.toast.warn('Processo eletrônico não encontrado para o número informado.')
				this.loading = false
				return
			}

			await this.loadProcess(process.id, this.demandId)
			this.ensureIncludeDocumentDraft()
		} finally {
			this.loading = false
		}
	}

	async searchIncludeModeProcesses(term = '', page = 0): Promise<void> {
		this.includeModeProcessesLoading = true

		try {
			this.includeModeProcessTerm = term
			const params = new HttpParams().set('page', page).set('size', 10)
			const query = new ElectronicProcessSearch({
				externalCode: term || null,
				statuses: [ElectronicProcessStatus.CREATED, ElectronicProcessStatus.PUBLISHED]
			})

			const response = await firstValueFrom(this.electronicProcessService.paginate(query, params))
			this.includeModeProcessPage = response
			this.includeModeProcesses = response.content ?? []
		} finally {
			this.includeModeProcessesLoading = false
		}
	}

	async onIncludeModeProcessSearch(term: string): Promise<void> {
		if (!this.includeDocument) {
			return
		}

		await this.searchIncludeModeProcesses(term?.trim() ?? '', 0)
	}

	async onIncludeModeProcessPageChange(page: number): Promise<void> {
		if (!this.includeDocument) {
			return
		}

		await this.searchIncludeModeProcesses(this.includeModeProcessTerm, page)
	}

	async selectIncludeModeProcess(process: ElectronicProcess): Promise<void> {
		if (!process?.id) {
			return
		}

		this.includeModeProcessId = process.id
		await this.loadProcess(process.id, null)
		this.ensureIncludeDocumentDraft()
	}

	async onIncludeModeProcessChange(processId: string | null): Promise<void> {
		this.includeModeProcessId = processId

		if (!processId) {
			return
		}

		await this.loadProcess(processId, null)
	}

	private async tryIncludeDocumentRequest(): Promise<void> {
		if (!this.canConfirmIncludeDocument) {
			return
		}

		const includeDraftControl = this.getIncludeDocumentDraftControl()
		const sourceId = includeDraftControl?.get('sourceId')?.value ?? this.includeDocumentRequest?.sourceId
		const alreadyIncluded = this.documentsForm.controls.some(control => {
			const controlId = control.get('id')?.value
			const controlSourceId = control.get('sourceId')?.value
			return !!controlId && controlSourceId === sourceId
		})

		if (alreadyIncluded) {
			this.toast.info('Este documento já foi incluído no processo eletrônico.')
			this.includeDocumentHandled = true
			return
		}

		this.creatingDocument = true

		try {
			const payload = includeDraftControl
				? this.buildDocumentPayload(includeDraftControl.getRawValue())
				: new ElectronicProcessDocumentRequest({
						...this.includeDocumentRequest,
						ordering: this.nextOrderingForSourceType(
							Number(this.includeDocumentRequest?.sourceType ?? ElectronicProcessDocumentSourceType.OTHER) as ElectronicProcessDocumentSourceType
						)
					})

			await firstValueFrom(this.electronicProcessService.saveDocuments(this.process.id, [payload]))
			this.toast.success('Documento incluído no processo eletrônico com sucesso.')
			this.includeDocumentHandled = true
			await this.reloadCurrentProcess()
		} finally {
			this.creatingDocument = false
		}
	}

	private ensureIncludeDocumentDraft(): void {
		if (!this.includeDocument || this.includeDocumentHandled || !this.includeDocumentRequest || !this.process?.id) {
			return
		}

		const sourceId = this.includeDocumentRequest.sourceId

		if (!sourceId) {
			return
		}

		const alreadyIncluded = this.documentsForm.controls.some(control => {
			const value = control.getRawValue()
			return !!value.id && value.sourceId === sourceId
		})

		if (alreadyIncluded) {
			this.includeDocumentHandled = true
			this.toast.info('Este documento já foi incluído no processo eletrônico.')
			return
		}

		const existingDraft = this.getIncludeDocumentDraftControl()

		if (existingDraft) {
			this.enableIncludeDocumentDraftControls(existingDraft as FormGroup)
			this.scheduleIncludeDocumentFormScroll(sourceId)
			return
		}

		this.includeDocumentDraftSourceId = sourceId

		this.documentsForm.push(
			this.createDocumentGroup(
				new ElectronicProcessDocument({
					id: null,
					title: this.includeDocumentRequest.title ?? 'Documento',
					description: this.includeDocumentRequest.description ?? null,
					ordering: this.nextOrderingForSourceType(
						Number(this.includeDocumentRequest.sourceType ?? ElectronicProcessDocumentSourceType.DEMAND) as ElectronicProcessDocumentSourceType
					),
					status: ElectronicProcessDocumentStatus.CREATED,
					sourceId,
					sourceType: this.includeDocumentRequest.sourceType ?? ElectronicProcessDocumentSourceType.DEMAND,
					visibleType: this.includeDocumentRequest.visibleType ?? ElectronicProcessDocumentVisibleType.PUBLIC,
					date: this.includeDocumentRequest.date ?? moment().format('YYYY-MM-DD')
				})
			)
		)

		this.recalculateDocumentOrdering()
		this.enableIncludeDocumentDraftControls(this.getIncludeDocumentDraftControl() as FormGroup)
		this.scheduleIncludeDocumentFormScroll(sourceId)
	}

	private getIncludeDocumentDraftControl(): FormGroup | null {
		if (!this.includeDocumentDraftSourceId) {
			return null
		}

		const control = this.documentsForm.controls.find(item => {
			const value = item.getRawValue()
			return !value.id && value.sourceId === this.includeDocumentDraftSourceId
		})

		return (control as FormGroup) ?? null
	}

	private enableIncludeDocumentDraftControls(control: FormGroup | null): void {
		if (!control) {
			return
		}

		control.enable()
		control.get('id')?.disable()
		control.get('status')?.disable()
		control.get('sourceId')?.disable()
		control.get('sourceType')?.disable()
	}

	private nextOrderingForSourceType(_sourceType: ElectronicProcessDocumentSourceType, formArray = this.documentsForm): number {
		const maxOrdering = formArray.controls.reduce((max, control) => Math.max(max, Number(control.get('ordering')?.value ?? 0)), 0)

		return maxOrdering + 1
	}

	private async persistDocumentsChanges(showSuccessToast = false): Promise<void> {
		if (!this.process?.id) {
			return
		}

		const changedControls = this.getChangedDocumentControls()

		for (const control of changedControls) {
			const value = control.getRawValue()

			await firstValueFrom(
				this.electronicProcessService.updateDocument(
					this.process.id,
					value.id,
					new ElectronicProcessDocumentPartialRequest(this.buildDocumentPayload(value))
				)
			)
		}

		if (showSuccessToast) {
			this.toast.success('Documentos atualizados com sucesso.')
		}
	}

	private buildDocumentPayloadData(value: any): Record<string, any> {
		const metadataPage = value?.metadata?.page ? new ElectronicProcessDocumentPage(value.metadata.page) : null
		const metadataAttachments = (value?.metadata?.attachments ?? []).map((attachment: ElectronicProcessDocumentAttachment) => ({
			...attachment,
			page: attachment?.page ? new ElectronicProcessDocumentPage(attachment.page) : null
		}))

		const metadata =
			metadataPage || metadataAttachments.length
				? new ElectronicProcessDocumentMetadata({
						page: metadataPage,
						attachments: metadataAttachments
					})
				: null

		return {
			title: value.title?.trim() || null,
			description: value.description?.trim() || null,
			ordering: Number(value.ordering),
			sourceId: value.sourceId,
			sourceType: Number(value.sourceType),
			visibleType: Number(value.visibleType),
			date: value.date,
			...(metadata ? { metadata } : {})
		}
	}

	private buildDocumentPayloadSnapshot(value: any): string {
		return JSON.stringify(this.buildDocumentPayloadData(value))
	}

	private captureDocumentsSnapshot(): void {
		this.originalDocumentPayloadById.clear()

		for (const control of this.documentsForm.controls) {
			const value = control.getRawValue()

			if (!value.id) {
				continue
			}

			this.originalDocumentPayloadById.set(value.id, this.buildDocumentPayloadSnapshot(value))
		}
	}

	private getChangedDocumentControls(): FormGroup[] {
		return this.documentsForm.controls.filter(control => {
			const value = control.getRawValue()

			if (!value.id) {
				return false
			}

			return this.originalDocumentPayloadById.get(value.id) !== this.buildDocumentPayloadSnapshot(value)
		}) as FormGroup[]
	}

	private async runDocumentAction(callback: () => Promise<void>): Promise<void> {
		this.submittingDocuments = true

		try {
			await callback()
			await this.reloadCurrentProcess()
		} finally {
			this.submittingDocuments = false
		}
	}

	private async searchDemandIntentDocuments(term: string): Promise<void> {
		if (!term) {
			this.linkedDemandIntentDocuments = this.linkedDemand && typeof this.linkedDemand !== 'string' ? [this.linkedDemand] : []
			return
		}

		this.linkedDemandLoading = true

		try {
			const response = await firstValueFrom(this.demandIntentDocumentService.paginate(new DemandIntentDocumentSearch({ externalCode: term })))
			this.linkedDemandIntentDocuments = response.content ?? []

			if (this.linkedDemand && typeof this.linkedDemand !== 'string') {
				this.appendLinkedDemandOption(this.linkedDemand)
			}
		} finally {
			this.linkedDemandLoading = false
		}
	}

	private async resolveLinkedDemandByExternalCode(externalCode: string): Promise<void> {
		const normalizedExternalCode = externalCode?.trim() ?? ''

		if (!normalizedExternalCode) {
			this.linkedDemand = null
			this.form.get('demandIntentDocumentId')?.setValue(null, { emitEvent: false })

			if (this.prefillDocuments) {
				this.documentsForm.clear()
			}
			return
		}

		this.linkedDemandLoading = true

		try {
			const response = await firstValueFrom(
				this.demandIntentDocumentService.paginate(new DemandIntentDocumentSearch({ externalCode: normalizedExternalCode }))
			)

			const demand =
				response.content?.find(item => item.externalCode?.trim().toUpperCase() === normalizedExternalCode.toUpperCase()) ??
				response.content?.[0] ??
				null

			if (!demand?.id) {
				this.linkedDemand = normalizedExternalCode
				this.form.get('demandIntentDocumentId')?.setValue(null, { emitEvent: false })

				if (this.prefillDocuments) {
					this.documentsForm.clear()
				}
				return
			}

			this.linkedDemand = demand
			this.appendLinkedDemandOption(demand)
			this.form.get('demandIntentDocumentId')?.setValue(demand.id, { emitEvent: false })
			this.syncUnitFromDemand(demand, true)

			if (this.prefillDocuments) {
				await this.fillDocumentsFromDemandReports(demand.id)
			}
		} finally {
			this.linkedDemandLoading = false
		}
	}

	private async syncLinkedDemandById(demandId: string, forcePrefill = false): Promise<void> {
		this.linkedDemandLoading = true

		try {
			const demand = await firstValueFrom(this.demandIntentDocumentService.detail(demandId))
			this.linkedDemand = demand
			this.appendLinkedDemandOption(demand)
			this.syncUnitFromDemand(demand, this.isCreateMode || !this.form.get('unitId')?.value)

			if (this.prefillDocuments && (forcePrefill || this.documentsForm.length === 0)) {
				await this.fillDocumentsFromDemandReports(demand.id)
			}
		} finally {
			this.linkedDemandLoading = false
		}
	}

	private async fillDocumentsFromDemandReports(demandId: string): Promise<void> {
		this.prefillingDemandDocuments = true

		try {
			const reports = await firstValueFrom(this.demandIntentDocumentReportService.reports(demandId))
			const signedReports = reports.filter(report => report.signature || report.signatureDone)
			const sortedReports = [...signedReports].sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0))

			this.documentsForm.clear()

			sortedReports.forEach((report, index) => {
				this.documentsForm.push(this.createDemandReportGroup(report, index + 1))
			})

			this.updateDocumentsReadonlyState()
		} finally {
			this.prefillingDemandDocuments = false
		}
	}

	private createDemandReportGroup(report: DemandIntentDocumentReport, fallbackOrdering: number): FormGroup {
		return new FormGroup({
			id: new FormControl<string | null>(null),
			submitting: new FormControl<boolean>(false),
			title: new FormControl<string | null>(this.resolveReportTitle(report)),
			description: new FormControl<string | null>(null),
			ordering: new FormControl<number>(fallbackOrdering, Validators.compose([Validators.required])),
			status: new FormControl<ElectronicProcessDocumentStatus>(ElectronicProcessDocumentStatus.CREATED),
			sourceId: new FormControl<string | null>(report.id),
			sourceType: new FormControl<ElectronicProcessDocumentSourceType>(
				ElectronicProcessDocumentSourceType.DEMAND,
				Validators.compose([Validators.required])
			),
			visibleType: new FormControl<ElectronicProcessDocumentVisibleType>(
				ElectronicProcessDocumentVisibleType.PUBLIC,
				Validators.compose([Validators.required])
			),
			externalCode: new FormControl<string | null>(report.order ?? null),
			date: new FormControl<string | null>(report.date ?? new Date().toISOString().slice(0, 10), Validators.compose([Validators.required]))
		})
	}

	private resolveReportTitle(report: DemandIntentDocumentReport): string {
		return this.templateUtil.listDocuments.find(document => document.type === report.type)?.name ?? report.title
	}

	private appendLinkedDemandOption(demand: DemandIntentDocument): void {
		if (!demand?.id || this.linkedDemandIntentDocuments.some(item => item.id === demand.id)) {
			return
		}

		this.linkedDemandIntentDocuments = [demand, ...this.linkedDemandIntentDocuments]
	}

	private syncUnitFromDemand(demand: DemandIntentDocument | null, force = false): void {
		if (!demand?.unit?.id || !this.canEditProcess) {
			return
		}

		const unitIdControl = this.form.get('unitId')
		const currentUnitId = unitIdControl?.value ?? null

		if (!force && currentUnitId) {
			return
		}

		unitIdControl?.setValue(demand.unit.id)
		unitIdControl?.markAsDirty()
	}

	private clearDocumentDragInteraction(): void {
		if (this.dragActivationTimeout) {
			clearTimeout(this.dragActivationTimeout)
			this.dragActivationTimeout = null
		}

		if (this.dragHandleElement && this.dragPointerId != null && this.dragHandleElement.hasPointerCapture?.(this.dragPointerId)) {
			this.dragHandleElement.releasePointerCapture(this.dragPointerId)
		}

		this.dragPressIndex = null
		this.dragActiveIndex = null
		this.dragPointerId = null
		this.dragHandleElement = null
	}

	private highlightMovedDocument(index: number, targetIndex: number, direction: -1 | 1): void {
		this.movingDocumentFromIndex = index
		this.movingDocumentToIndex = targetIndex
		this.movingDocumentDirection = direction

		if (this.moveDocumentAnimationTimeout) {
			clearTimeout(this.moveDocumentAnimationTimeout)
		}

		setTimeout(() => {
			const targetElement = this.documentItems?.get(targetIndex)?.nativeElement

			targetElement?.scrollIntoView({
				behavior: 'smooth',
				block: 'center'
			})
		}, 30)

		this.moveDocumentAnimationTimeout = setTimeout(() => {
			this.movingDocumentFromIndex = null
			this.movingDocumentToIndex = null
			this.movingDocumentDirection = null
			this.moveDocumentAnimationTimeout = null
		}, 480)
	}

	private scheduleIncludeDocumentFormScroll(sourceId: string | null): void {
		if (!sourceId) {
			return
		}

		this.includeDocumentScrollSourceId = sourceId

		if (this.includeDocumentScrollRetryTimeout) {
			clearTimeout(this.includeDocumentScrollRetryTimeout)
			this.includeDocumentScrollRetryTimeout = null
		}

		this.tryScrollToIncludeDocumentForm(0)
	}

	private tryScrollToIncludeDocumentForm(attempt: number): void {
		const maxAttempts = 40

		if (!this.includeDocumentScrollSourceId) {
			return
		}

		if (this.loading || this.prefillingDemandDocuments || this.createDocument) {
			this.retryScrollToIncludeDocumentForm(attempt, maxAttempts)
			return
		}

		const targetIndex = this.findDocumentIndexBySourceId(this.includeDocumentScrollSourceId)
		const targetElement = targetIndex >= 0 ? this.documentItems?.get(targetIndex)?.nativeElement : null

		if (!targetElement) {
			this.retryScrollToIncludeDocumentForm(attempt, maxAttempts)
			return
		}

		targetElement.scrollIntoView({
			behavior: 'smooth',
			block: 'end'
		})
		this.scrollContainerToBottom(targetElement)

		this.includeDocumentScrollSourceId = null
		this.includeDocumentScrollRetryTimeout = null
	}

	private retryScrollToIncludeDocumentForm(attempt: number, maxAttempts: number): void {
		if (attempt >= maxAttempts) {
			this.includeDocumentScrollSourceId = null
			this.includeDocumentScrollRetryTimeout = null
			return
		}

		this.includeDocumentScrollRetryTimeout = setTimeout(() => {
			this.tryScrollToIncludeDocumentForm(attempt + 1)
		}, 120)
	}

	private findDocumentIndexBySourceId(sourceId: string): number {
		return this.documentsForm.controls.findIndex(control => control.get('sourceId')?.value === sourceId)
	}

	private scrollContainerToBottom(targetElement: HTMLElement): void {
		setTimeout(() => {
			const scrollParent = this.findNearestScrollableParent(targetElement)
			if (!scrollParent) {
				return
			}

			scrollParent.scrollTo({
				top: scrollParent.scrollHeight,
				behavior: 'smooth'
			})
		}, 220)
	}

	private findNearestScrollableParent(element: HTMLElement): HTMLElement | null {
		let current: HTMLElement | null = element.parentElement

		while (current) {
			const style = window.getComputedStyle(current)
			const canScroll = /(auto|scroll)/.test(style.overflowY) && current.scrollHeight > current.clientHeight
			if (canScroll) {
				return current
			}
			current = current.parentElement
		}

		return null
	}

	private updateDocumentsReadonlyState(): void {
		this.documentsForm.controls.forEach(control => {
			if (this.documentsReadonly) {
				control.disable()
			} else {
				control.enable()
				control.get('id')?.disable()
				control.get('status')?.disable()
				control.get('sourceId')?.disable()
				control.get('sourceType')?.disable()

				if (!this.canEditDocumentList && this.canEditPublishedDocumentVisibility) {
					control.get('title')?.disable()
					control.get('description')?.disable()
					control.get('date')?.disable()
					control.get('ordering')?.disable()
				}
			}
		})

		if (this.includeDocument) {
			this.enableIncludeDocumentDraftControls(this.getIncludeDocumentDraftControl())
		}
	}

	private async runProcessAction(callback: () => Promise<void>, reloadAfter = true): Promise<void> {
		this.submittingProcess = true

		try {
			await callback()

			if (reloadAfter && this.process?.id) {
				await this.loadProcess(this.process.id)
			}
		} finally {
			this.submittingProcess = false
		}
	}

	// --- Paginação (spec 002)

	/** Ouve o tópico do processo aberto e busca a execução mais recente; sem conexão, o poller cobre por REST. */
	private async watchPagination(): Promise<void> {
		const processId = this.process?.id

		if (!processId) {
			this.stopWatchingPagination()
			return
		}

		if (this.paginationProcessId !== processId) {
			this.paginationProcessId = processId
			this.paginationRun = null
			this.paginationRunLoaded = false
			this.paginationSocket.connect(this.accountService.tenantId)
			this.paginationSocket.subscribeProcess(processId)

			this.paginationPoller?.stop()
			this.paginationPoller = new RealtimeFallbackPoller({
				isConnected: () => this.paginationSocket.connected || !this.paginationInProgress,
				resync: () => void this.resyncPagination().finally(() => this.paginationPoller?.settle())
			})
			this.paginationPoller.start()
		}

		try {
			this.paginationRun = await firstValueFrom(this.paginationService.latest(processId))
			this.paginationRunLoaded = true
		} catch {
			// Sem o histórico, a tela segue com a situação dos documentos.
		}
	}

	private stopWatchingPagination(): void {
		this.paginationPoller?.stop()
		this.paginationPoller = undefined

		if (this.paginationProcessId) {
			this.paginationSocket.unsubscribeProcess(this.paginationProcessId)
		}

		this.paginationProcessId = null
		this.paginationRun = null
		this.paginationRunLoaded = false
	}

	/** Há contagem em curso: execução ativa, processo publicando ou documento esperando a vez. */
	private get paginationInProgress(): boolean {
		return (
			!!this.paginationRun?.active ||
			this.process?.status === ElectronicProcessStatus.IN_PUBLISH ||
			[...this.documentsById.values()].some(document => document.paginationState && !document.paginationFailed)
		)
	}

	private async resyncPagination(): Promise<void> {
		const processId = this.process?.id

		if (!processId || !this.paginationInProgress) {
			return
		}

		try {
			const latest = await firstValueFrom(this.paginationService.latest(processId))

			if (
				latest &&
				(latest.id !== this.paginationRun?.id ||
					latest.status !== this.paginationRun?.status ||
					latest.processedUnits !== this.paginationRun?.processedUnits)
			) {
				await this.onPaginationEvent(latest)
			}
		} catch {
			/* o próximo ciclo tenta de novo */
		}
	}

	private async onPaginationEvent(run: ElectronicProcessPaginationRun): Promise<void> {
		if (!this.process?.id || run.processId !== this.process.id) {
			return
		}

		const previous = this.paginationRun

		// Evento atrasado de uma execução mais antiga (a simulação descartada, por exemplo) não substitui a atual.
		if (previous && previous.id !== run.id && (run.createdAt ?? '') < (previous.createdAt ?? '')) {
			return
		}

		// A execução anterior terminou e a próxima já começou (a inclusão seguinte da fila): o término dela pode ter
		// passado sem evento, com o socket fora.
		const previousFinished = !!previous?.active && previous.id !== run.id
		this.paginationRun = run

		if (run.active) {
			if (previousFinished) await this.refreshAfterPagination()
			return
		}

		const wasRunning = previous?.id === run.id ? previous.active : true
		await this.refreshAfterPagination()

		if (wasRunning) {
			this.notifyPaginationFinished(run)
		}
	}

	private notifyPaginationFinished(run: ElectronicProcessPaginationRun): void {
		switch (run.type) {
			case ElectronicProcessPaginationRunType.PUBLISH:
				if (run.status === ElectronicProcessPaginationRunStatus.COMPLETED) {
					this.toast.success('Processo eletrônico publicado com sucesso.')

					if (this.isDemandContext) {
						this.processActionCompleted.emit()
					}
				} else if (run.failed) {
					this.toast.error('A publicação falhou. Confira a lista de documentos com problema, corrija e publique novamente.')
				}
				break

			case ElectronicProcessPaginationRunType.INCLUDE:
				if (run.status === ElectronicProcessPaginationRunStatus.COMPLETED) {
					this.toast.success('Paginação dos documentos incluídos concluída.')
				} else if (run.failed) {
					this.toast.warn('A paginação de um documento incluído falhou. Tente de novo ou remova o documento.')
				}
				break

			case ElectronicProcessPaginationRunType.SIMULATION:
				// Com a simulação aberta, o próprio modal mostra o resultado.
				if (!this.simulationModalOpen && run.status === ElectronicProcessPaginationRunStatus.SIMULATED) {
					this.toast.info('A simulação de renumeração está pronta para revisão.')
				}
				break
		}
	}

	/**
	 * Recarrega o processo depois de uma paginação, sem o carregamento da tela inteira. Com alterações não salvas nos
	 * documentos, a lista fica como está (só a situação de paginação muda), para não perder a edição: as folhas
	 * aparecem depois de salvar ou descartar.
	 */
	private async refreshAfterPagination(): Promise<void> {
		if (!this.process?.id) {
			return
		}

		const process = await firstValueFrom(this.electronicProcessService.detail(this.process.id))
		this.process = process

		if (this.canEditProcess) {
			this.form.enable()
		} else {
			this.form.disable()
		}

		if (this.getChangedDocumentControls().length === 0) {
			this.rebuildDocumentsForm(process.documents ?? [])
		} else {
			this.indexDocuments(process.documents ?? [])
			this.toast.info('A paginação dos documentos foi atualizada. Salve ou descarte suas alterações para ver as folhas novas.')
		}

		this.paginationHistory?.reload()
	}

	private indexDocuments(documents: ElectronicProcessDocument[]): void {
		this.documentsById = new Map(documents.filter(document => document.id).map(document => [document.id!, document]))
	}

	/** O 503 não passa pelo toast do interceptor: o serviço de paginação estava fora e o pedido foi desfeito. */
	private handlePaginationRequestError(error: unknown): void {
		if (error instanceof HttpErrorResponse && (error.status === 503 || error.status === 0)) {
			this.toast.error('O serviço de paginação está indisponível no momento. Tente novamente em instantes.')
		}
	}

	get currentProcess(): ElectronicProcess | null {
		return this.process
	}

	onOtherDocumentFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement
		const file = input.files?.[0] ?? null

		if (!file) {
			this.documentOtherFileControl.setValue(null)
			return
		}

		if (file.type !== 'application/pdf') {
			this.documentOtherFileControl.setValue(null)
			input.value = ''
			this.toast.warn('Formato invalido, informe um arquivo em PDF.')
			return
		}

		const maxSizeInBytes = 20 * 1024 * 1024

		if (file.size > maxSizeInBytes) {
			this.documentOtherFileControl.setValue(null)
			input.value = ''
			this.toast.warn('O tamanho do arquivo não pode ser maior que 20MB.')
			return
		}

		this.documentOtherFileControl.setValue(file)
		this.documentOtherFileControl.markAsDirty()
		this.documentOtherFileControl.markAsTouched()
	}

	private async uploadOtherDocumentAttachments(): Promise<{ sourceId: string; metadata: ElectronicProcessDocumentMetadata } | null> {
		const file = this.documentOtherFileControl.value

		if (!file) {
			this.toast.error('Informe um anexo em PDF para incluir o documento.')
			return null
		}

		const sourceId = uuid()
		const response = await this.uploadS3Service.uploadAttachment(sourceId, file, AttachmentServiceType.MANAGEMENT_PROCESS)

		if (response.status === UploadStatus.ERROR) {
			this.toast.warn('Desculpe, não conseguimos enviar o anexo. Se possível, selecione outro arquivo.')
			return null
		}

		const termAttachment = await this.uploadOtherDocumentTermAttachment()

		if (!termAttachment) {
			return null
		}

		return {
			sourceId,
			metadata: new ElectronicProcessDocumentMetadata({
				attachments: [termAttachment]
			})
		}
	}

	private async uploadOtherDocumentTermAttachment(): Promise<ElectronicProcessDocumentAttachment | null> {
		const termAttachmentId = uuid()
		const termBlob = await this.reportTermJoinedForOtherDocument()

		if (!termBlob) {
			this.toast.warn('Não foi possível gerar o termo de juntada obrigatório para o documento.')
			return null
		}

		const processCode = (this.externalCodeControl.value || this.process?.externalCode || 'processo').toString().trim() || 'processo'
		const safeProcessCode = processCode.replace(/[^a-zA-Z0-9-_]/g, '-')
		const filename = `termo-juntada-${safeProcessCode}.pdf`
		const termFile = new File([termBlob], filename, { type: 'application/pdf' })
		const uploadResponse = await this.uploadS3Service.uploadAttachment(termAttachmentId, termFile, AttachmentServiceType.MANAGEMENT_PROCESS)

		if (uploadResponse.status === UploadStatus.ERROR) {
			this.toast.warn('Não foi possível enviar o termo de juntada obrigatório para o documento.')
			return null
		}

		return {
			id: termAttachmentId,
			name: 'Termo de juntada',
			filename,
			size: termFile.size,
			created: moment().toISOString()
		}
	}

	private async reportTermJoinedForOtherDocument(): Promise<Blob | null> {
		const account = this.accountService.account
		const organization = account?.organization

		const selectedUnitId = this.unitControl.value
		const address = organization?.address
		const state = this.label.fromState(address?.state)?.data?.acronym
		const city = (address?.city ?? 'Cidade não informada').toUpperCase()
		const cityLabel = state ? `${city}/${state}` : city
		const formattedAddress = address ? this.label.formattedAddress(address) : ''
		const entityName = (organization?.simpleName || organization?.name || '').toUpperCase()
		const selectedUnitName = this.units.find(item => item.id === selectedUnitId)?.name ?? null
		const unitName = (selectedUnitName || this.process?.unitName || 'UNIDADE NÃO INFORMADA').toUpperCase()

		return this.reportService.reportTermJoined({
			entity: entityName,
			unit: unitName,
			address: formattedAddress,
			document: this.mask.transform(organization.document, 'CPF_CNPJ'),
			date: moment().format('DD [de] MMMM [de] YYYY'),
			city: cityLabel
		})
	}

	get isCreateMode(): boolean {
		return !this.process?.id
	}

	get canEditProcess(): boolean {
		return !this.process || this.process.status !== ElectronicProcessStatus.PUBLISHED
	}

	get canManageDocuments(): boolean {
		if (this.demand?.publishStatus === DemandIntentDocumentPublishStatus.DONE) {
			return false
		}

		if (this.includeDocument) {
			return !!this.process?.id && this.process.status !== ElectronicProcessStatus.PUBLISHED
		}

		return !!this.process?.id && this.process.status !== ElectronicProcessStatus.PUBLISHED
	}

	get canAddDocuments(): boolean {
		if (this.demand?.publishStatus === DemandIntentDocumentPublishStatus.DONE) {
			return false
		}

		if (!this.process?.id) {
			return false
		}

		if (this.includeDocument) {
			return this.process.status !== ElectronicProcessStatus.CANCELED
		}

		return this.process.status !== ElectronicProcessStatus.CANCELED
	}

	get canEditDocumentList(): boolean {
		if (this.demand?.publishStatus === DemandIntentDocumentPublishStatus.DONE) {
			return false
		}

		if (this.includeDocument) {
			return !!this.process?.id && this.process.status !== ElectronicProcessStatus.PUBLISHED
		}

		return !this.process || this.process.status !== ElectronicProcessStatus.PUBLISHED
	}

	get canEditPublishedDocumentVisibility(): boolean {
		if (this.demand?.publishStatus === DemandIntentDocumentPublishStatus.DONE) {
			return false
		}

		if (this.includeDocument) {
			return !!this.process?.id && this.process.status === ElectronicProcessStatus.PUBLISHED
		}

		return !!this.process?.id && this.process.status === ElectronicProcessStatus.PUBLISHED
	}

	get canPersistDocumentChanges(): boolean {
		if (this.canManageDocuments) {
			return true
		}

		return this.canEditPublishedDocumentVisibility
	}

	get canReorderDocuments(): boolean {
		if (this.includeDocument && !this.includeDocumentHandled) {
			return false
		}

		return !!this.process?.id && this.process.status !== ElectronicProcessStatus.PUBLISHED
	}

	get isReadonly(): boolean {
		return !this.canEditProcess
	}

	get documentsReadonly(): boolean {
		return !this.canEditDocumentList && !this.canEditPublishedDocumentVisibility
	}

	get prefillDocuments(): boolean {
		return this.isCreateMode
	}

	get lockDemandAndUnit(): boolean {
		return !!(this.demand?.id || this.demandId)
	}

	private get isDemandContext(): boolean {
		return !!this.demand?.id
	}

	get processBusy(): boolean {
		return this.submittingProcess || !!this.submittingProcess || this.submittingDocuments || this.linkedDemandLoading || this.downloadingAllAttachments
	}

	get canDownloadAllAttachments(): boolean {
		for (const control of this.documentsForm.controls as FormGroup[]) {
			if (control.get('sourceId')?.value) {
				return true
			}

			if (this.getDocumentAttachments(control).some(attachment => !!attachment?.id)) {
				return true
			}
		}

		return false
	}

	private getProcessAttachmentFiles(): Array<{ id: string; name: string }> {
		const files: Array<{ id: string; name: string }> = []
		const ids = new Set<string>()
		const names = new Map<string, number>()

		for (const [index, documentControl] of (this.documentsForm.controls as FormGroup[]).entries()) {
			const rawValue = documentControl.getRawValue()
			const ordering = Number(rawValue?.ordering ?? index + 1)
			const documentTitle = this.sanitizeZipFilename(rawValue?.externalCode || rawValue?.title || 'documento')
			const sourceId = `${rawValue?.sourceId ?? ''}`.trim()

			if (sourceId && !ids.has(sourceId)) {
				ids.add(sourceId)
				files.push({ id: sourceId, name: this.uniqueZipFilename(`${ordering} - ${documentTitle}.pdf`, names) })
			}

			const attachments = this.getDocumentAttachments(documentControl)
			for (const [attachmentIndex, attachment] of attachments.entries()) {
				const attachmentId = `${attachment?.id ?? ''}`.trim()
				if (!attachmentId || ids.has(attachmentId)) {
					continue
				}

				ids.add(attachmentId)
				const attachmentName = this.sanitizeZipFilename(attachment.filename || attachment.name || `anexo-${attachmentIndex + 1}`)
				const filename = this.withPdfExtension(`${ordering} - ${documentTitle} - ${attachmentName}`)
				files.push({ id: attachmentId, name: this.uniqueZipFilename(filename, names) })
			}
		}

		return files
	}

	private sanitizeZipFilename(value?: string): string {
		const normalized = `${value ?? ''}`
			.replace(/[\\/:*?"<>|]/g, '-')
			.replace(/\s+/g, ' ')
			.trim()

		return normalized || 'arquivo'
	}

	private withPdfExtension(filename: string): string {
		return /\.[a-z0-9]{2,8}$/i.test(filename) ? filename : `${filename}.pdf`
	}

	private openMergedDocumentPreview(file: Blob, filename: string): void {
		if (!this.modalMergedDocument) {
			this.toast.warn('Não foi possível abrir a visualização do documento.')
			return
		}

		this.clearMergedDocumentPreview()

		this.mergedDocumentFilename = filename
		this.mergedDocumentBlob = file
		this.mergedDocumentObjectUrl = URL.createObjectURL(file)
		this.mergedDocumentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.mergedDocumentObjectUrl)

		const modalRef = this.modal.open(this.modalMergedDocument, { fullscreen: true })
		modalRef.closed.subscribe(() => this.clearMergedDocumentPreview())
		modalRef.dismissed.subscribe(() => this.clearMergedDocumentPreview())
	}

	private clearMergedDocumentPreview(): void {
		this.mergedDocumentUrl = null
		this.mergedDocumentFilename = null
		this.mergedDocumentBlob = null

		if (this.mergedDocumentObjectUrl) {
			URL.revokeObjectURL(this.mergedDocumentObjectUrl)
			this.mergedDocumentObjectUrl = null
		}
	}

	downloadPreviewedDocument(): void {
		if (!this.mergedDocumentBlob || !this.mergedDocumentFilename) {
			return
		}

		saveAs(this.mergedDocumentBlob, this.mergedDocumentFilename)
	}

	private uniqueZipFilename(filename: string, map: Map<string, number>): string {
		const lastDot = filename.lastIndexOf('.')
		const base = lastDot > 0 ? filename.slice(0, lastDot) : filename
		const extension = lastDot > 0 ? filename.slice(lastDot) : ''

		const count = (map.get(filename) ?? 0) + 1
		map.set(filename, count)

		if (count === 1) {
			return filename
		}

		return `${base} (${count})${extension}`
	}

	/** Reprocessar (spec 002, RF 12): processo publicado ou com erro na publicação, sem paginação em andamento. */
	get canReprocessPagination(): boolean {
		if (!this.process?.id || this.paginationRun?.active) {
			return false
		}

		switch (this.process.status) {
			case ElectronicProcessStatus.PUBLISHED:
			case ElectronicProcessStatus.ERROR_PUBLISH:
				return true
			// Publicação antiga interrompida (sem execução ativa): o servidor publica de novo. Só depois de saber que não há
			// contagem em curso.
			case ElectronicProcessStatus.IN_PUBLISH:
				return this.paginationRunLoaded
			default:
				return false
		}
	}

	/** Contagem de publicação, inclusão ou simulação em andamento, para a faixa de progresso. */
	get paginationProgress(): ElectronicProcessPaginationRun | null {
		return this.paginationRun?.active ? this.paginationRun : null
	}

	/** A publicação que falhou, com a lista de documentos e motivos (RF 3). */
	get failedPublication(): ElectronicProcessPaginationRun | null {
		const run = this.paginationRun

		return this.process?.status === ElectronicProcessStatus.ERROR_PUBLISH && run?.type === ElectronicProcessPaginationRunType.PUBLISH && run.failed
			? run
			: null
	}

	/** Simulação pronta e no prazo, aguardando a revisão. */
	get pendingSimulation(): ElectronicProcessPaginationRun | null {
		const run = this.paginationRun

		return run?.type === ElectronicProcessPaginationRunType.SIMULATION && run.applicable ? run : null
	}

	get detailsInputsLoading(): boolean {
		return this.loading || this.linkedDemandLoading || this.prefillingDemandDocuments
	}

	get canOpenDocumentsTab(): boolean {
		if (this.includeDocument) {
			return true
		}

		return !this.isCreateMode || this.form.valid
	}

	get canConfirmIncludeDocument(): boolean {
		const includeDraftControl = this.getIncludeDocumentDraftControl()

		return (
			!!this.includeDocument &&
			!!this.process?.id &&
			!!this.includeDocumentRequest &&
			!!includeDraftControl &&
			!includeDraftControl.invalid &&
			!this.includeDocumentHandled &&
			!this.creatingDocument
		)
	}

	get showIncludeProcessSelection(): boolean {
		return !!this.includeDocument && !this.process?.id
	}

	canEditDocumentRow(documentControl: any): boolean {
		if (this.canEditDocumentList) {
			return true
		}

		if (!this.includeDocument) {
			return false
		}

		const value = documentControl?.getRawValue?.()
		return !!this.includeDocumentDraftSourceId && !value?.id && value?.sourceId === this.includeDocumentDraftSourceId
	}

	canEditDocumentVisibleType(documentControl: any): boolean {
		if (this.canEditDocumentRow(documentControl)) {
			return true
		}

		if (this.canEditPublishedDocumentVisibility) {
			const value = documentControl?.getRawValue?.()
			return !!value?.id
		}

		return false
	}

	get primaryActionLabel(): string {
		if (this.isCreateMode && this.activeTab === 'details') {
			return 'Ir para documentos'
		}

		if (this.isCreateMode) {
			return 'Salvar processo'
		}

		return 'Salvar detalhes'
	}

	get unitControl(): FormControl {
		return this.form.get('unitId') as FormControl
	}

	get unitControlInvalid(): boolean {
		return this.unitControl.invalid && (this.unitControl.dirty || this.unitControl.touched)
	}

	get externalCodeControl(): FormControl {
		return this.form.get('externalCode') as FormControl
	}

	get externalCodeControlInvalid(): boolean {
		return this.externalCodeControl.invalid && (this.externalCodeControl.dirty || this.externalCodeControl.touched)
	}

	get objectControl(): FormControl {
		return this.form.get('object') as FormControl
	}

	get objectControlInvalid(): boolean {
		return this.objectControl.invalid && (this.objectControl.dirty || this.objectControl.touched)
	}

	get demandIntentDocumentControl(): FormControl {
		return this.form.get('demandIntentDocumentId') as FormControl
	}

	get demandIntentDocumentControlInvalid(): boolean {
		return this.demandIntentDocumentControl.invalid && (this.demandIntentDocumentControl.dirty || this.demandIntentDocumentControl.touched)
	}

	get documentTitleControl(): FormControl {
		return this.documentForm.get('title') as FormControl
	}

	get documentTitleControlInvalid(): boolean {
		return this.documentTitleControl.invalid && (this.documentTitleControl.dirty || this.documentTitleControl.touched)
	}

	get documentSourceTypeControl(): FormControl {
		return this.documentForm.get('sourceType') as FormControl
	}

	get documentSourceTypeControlInvalid(): boolean {
		return this.documentSourceTypeControl.invalid && (this.documentSourceTypeControl.dirty || this.documentSourceTypeControl.touched)
	}

	get documentOtherFileControlInvalid(): boolean {
		return this.documentOtherFileControl.invalid && (this.documentOtherFileControl.dirty || this.documentOtherFileControl.touched)
	}

	get documentVisibleTypeControl(): FormControl {
		return this.documentForm.get('visibleType') as FormControl
	}

	get documentVisibleTypeControlInvalid(): boolean {
		return this.documentVisibleTypeControl.invalid && (this.documentVisibleTypeControl.dirty || this.documentVisibleTypeControl.touched)
	}

	get documentDateControl(): FormControl {
		return this.documentForm.get('date') as FormControl
	}

	get documentDateControlInvalid(): boolean {
		return this.documentDateControl.invalid && (this.documentDateControl.dirty || this.documentDateControl.touched)
	}

	get demandId() {
		return this.demand?.id
	}

	set demandId(value: string | undefined) {
		if (!this.demand) {
			this.demand = new DemandIntentDocument({ id: value })
		}

		this.demand.id = value
	}

	get isActiveAddDocuments() {
		return this.canAddDocuments
	}
}
